// requires a loader
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { T, useTranslate } from '@tolgee/react';
import { Collapse, Divider } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

const { Panel } = Collapse;

import { CheckSvg } from '../assets/icons';
import ExternalLinkSvg from '../assets/icons/ExternalLinkSvg';
import LandingPageSubjectCareerSvg from '../assets/icons/LandingPageSubjectCareerSvg';
import LandingPageSubjectELASvg from '../assets/icons/LandingPageSubjectELASvg';
import LandingPageSubjectMathSvg from '../assets/icons/LandingPageSubjectMathSvg';
import LandingPageSubjectMoreSvg from '../assets/icons/LandingPageSubjectMoreSvg';
import LandingPageSubjectScienceSvg from '../assets/icons/LandingPageSubjectScienceSvg';
import LandingPageSubjectSELSvg from '../assets/icons/LandingPageSubjectSELSvg';
import LandingPageSubjectSocialStudiesSvg from '../assets/icons/LandingPageSubjectSocialStudiesSvg';
import LandingPageSubjectWorldLanguagesSvg from '../assets/icons/LandingPageSubjectWorldLanguagesSvg';
import PlaySvg from '../assets/icons/PlaySvg';
import QuoteSvg from '../assets/icons/QuoteSvg';
import StarLandingPageBackground from '../assets/icons/StarLandingPageBackground';
import WaveLandingPageDiv from '../assets/icons/WaveLandingPageDiv';
import WaveLandingPageDiv3 from '../assets/icons/WaveLandingPageDiv3';
import WaveStarLandingPageBackground from '../assets/icons/WaveStarLandingPageBackground';
import aileen from '../assets/images/LandingPage/aileen.png';
import amy from '../assets/images/LandingPage/amy.png';
import becky from '../assets/images/LandingPage/becky.png';
import claudia from '../assets/images/LandingPage/Claudia.png';
import curiVideoCoverImage from '../assets/images/LandingPage/curiVideoCoverImage.png';
import dalton from '../assets/images/LandingPage/dalton.png';
import dave from '../assets/images/LandingPage/david.jpeg';
import erika from '../assets/images/LandingPage/erika.png';
import feedbackImg from '../assets/images/LandingPage/feedback.png';
import karyn from '../assets/images/LandingPage/karyn.png';
import medina from '../assets/images/LandingPage/medina.png';
import mercer from '../assets/images/LandingPage/mercer.png';
import pinecrest from '../assets/images/LandingPage/pinecrest.png';
import psusd from '../assets/images/LandingPage/psusd.png';
import rachel from '../assets/images/LandingPage/rachel.png';
import roysecity from '../assets/images/LandingPage/roysecity.png';
import salo from '../assets/images/LandingPage/salo.png';
import sequoia from '../assets/images/LandingPage/sequoia.jpg';
import slidesImg from '../assets/images/LandingPage/slides.png';
import testPrep from '../assets/images/LandingPage/test-prep.png';
import texasconnections from '../assets/images/LandingPage/texasconnections.png';
import vanguard from '../assets/images/LandingPage/vanguard.png';
import videoCover from '../assets/images/LandingPage/video-cover.png';
import willis from '../assets/images/LandingPage/willis.png';
import theme from '../assets/theme';
import { useBreakPoint } from '../hooks/useBreakPoint';
import { useLocale } from '../hooks/useLocale';
import { useMetrics } from '../hooks/useMetrics';
import useRouterMatch from '../hooks/useRouterMatch';
import { getTrackingParams } from '../utils/adsConversionTrackingUtils';
import { zIndexes } from '../utils/zIndexes';
import { Button } from './_atoms/Button';
import { IconWrapper } from './_atoms/IconWrapper';

const heroBackgroundVideo = '/hero-background-video.mp4';
const Container = styled.div`
  width: 100%;
`;

const LandingPageSection = styled.div<{
  $backgroundGradient?: string;
  $hasWave: boolean;
  $isMobileMainSection?: boolean;
}>`
  position: relative;
  padding-top: 50px;
  padding-bottom: 50px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.landingPageBeige};
  align-items: center;
  overflow-x: hidden;

  ${({ $backgroundGradient }) =>
    $backgroundGradient &&
    css`
      background: ${$backgroundGradient};
    `}

  ${({ $hasWave }) =>
    $hasWave &&
    css`
      margin-bottom: -2px;
    `}

  ${({ $isMobileMainSection }) =>
    $isMobileMainSection &&
    css`
      min-height: calc(100vh - 80px);
      justify-content: center;
      padding-top: 40px;
      padding-bottom: 40px;

      @media (max-width: 768px) {
        min-height: calc(100vh - 60px);
        padding-top: 20px;
        padding-bottom: 20px;
        gap: 0;
        margin-top: 0;
      }
    `}

  @media (max-width: 900px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
  }
`;

const GetStudentsExcitedSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.landingPageBeige};
  align-items: center;
  justify-content: center;
  height: calc(100dvh - 60px);
  overflow: hidden;
`;

const LandingPageContent = styled.div`
  padding: 0 20px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const baseHeadingStyles = css`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  max-width: 1000px;
  margin-right: auto;
  margin-left: auto;
  font-weight: 900;
  z-index: 1;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

const StyledH1 = styled.h1`
  ${baseHeadingStyles}
  margin-bottom: 0;
`;

const StyledH2 = styled.h2`
  ${baseHeadingStyles}
`;

const StyledH3 = styled.h3`
  ${baseHeadingStyles}
`;

const SectionSubtitle = styled.p`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  max-width: 900px;
  z-index: 1;
  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

// Animation for the rotating word
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOutUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const AnimatedWordContainer = styled.span`
  display: inline;
`;

const AnimatedWord = styled.span<{ $isExiting: boolean }>`
  display: inline;
  font-weight: 900;
  animation: ${({ $isExiting }) => ($isExiting ? fadeOutUp : fadeInUp)} 0.4s ease-out
    forwards;
`;

const HeroSubtitleText = styled.p`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  z-index: 1;
  margin: 0;
  max-width: 700px;
  color: white;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const TitleContainerOuter = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const TitleContainerBackground = styled.div`
  position: absolute;
  inset: 0;
  mask-image: linear-gradient(to bottom, transparent, black 20%, black 80%, transparent);
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  pointer-events: none;
`;

const TitleContainerBackgroundInner = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent);
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
`;

const TitleContainerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  padding: 100px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const ProvenOutcomesLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const HeroButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  z-index: 1;
  box-sizing: border-box;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 0 20px;
  }
`;

const HeaderMobileSectionTitle = styled.h1`
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 0;
  text-align: center;
  max-width: 1000px;
  margin-right: auto;
  margin-left: auto;
  font-weight: 900;
  z-index: 1;
`;

const WhiteHeaderMobileSectionTitle = styled(HeaderMobileSectionTitle)`
  color: white;
`;

const WhiteStyledH1 = styled(StyledH1)`
  color: white;
`;

const BackgroundVideoContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  width: 100%;
  object-fit: cover;
  height: 100%;
`;

const BackgroundImage = styled.img<{ $isVisible: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease;
  pointer-events: none;
`;

const HeaderSectionMobileSubtitle = styled.p<{ $width?: number }>`
  font-size: 20px;
  max-width: ${({ $width }) => ($width ? `${$width - 10}px` : '300px')};
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  margin-right: auto;
  margin-left: auto;
  z-index: 1;
`;

const useElementWidth = (content: React.ReactNode) => {
  const ref = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const measure = () =>
      ref.current && setWidth(ref.current.getBoundingClientRect().width);
    const timeoutId = setTimeout(measure, 0);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', measure);
    };
  }, [content]);

  return [ref, width] as const;
};

const MobileHeaderTitleSubtitle = ({
  titleContent,
  subtitleContent,
}: {
  titleContent: React.ReactNode;
  subtitleContent: React.ReactNode;
}) => {
  const [titleRef, titleWidth] = useElementWidth(titleContent);

  return (
    <>
      <HeaderMobileSectionTitle ref={titleRef as React.RefObject<HTMLHeadingElement>}>
        {titleContent}
      </HeaderMobileSectionTitle>
      <HeaderSectionMobileSubtitle $width={titleWidth}>
        {subtitleContent}
      </HeaderSectionMobileSubtitle>
    </>
  );
};

const ResponsiveCTA = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 40px;
  gap: 20px;
  align-items: center;
  justify-content: center;
`;

const WaveDivider = styled.div`
  width: 100%;
  height: fit-content;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const StarWaveBackground = styled.div`
  width: 100%;
  height: fit-content;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  svg {
    width: 100%;
  }
`;

const HeroCenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
`;

const ScrollingLogosContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 40px 0;
  position: relative;
`;
const TitleSubtitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  width: 100%;
`;

const ScrollingLogosTrack = styled.div`
  display: flex;
  gap: 60px;
  animation: scroll 30s linear infinite;
  width: max-content;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-33.333% - 13.333px));
    }
  }
`;

const LogoWrapper = styled.div`
  flex-shrink: 0;
  height: 100px;
  max-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const MichiganImpactCaseStudyTitle = (
  <T
    keyName="Michigan Impact Case Study Title"
    defaultValue="Instructional Shifts That Stick (MI)"
  />
);
export const ButlerUniversityCaseStudyTitle = (
  <T
    keyName="Butler University Case Study Title"
    defaultValue="Inclusive Engagement in Higher Ed (Butler University, IN)"
  />
);
export const TexasImpactCaseStudyTitle = (
  <T
    keyName="Texas Impact Case Study Title"
    defaultValue="22% Increase in Meets & Masters on STAAR (TX)"
  />
);
export const TexasImpactCaseStudyTitle2 = (
  <T
    keyName="Texas Impact Case Study Title 2"
    defaultValue="32% Growth in STAAR Scores (TX)"
  />
);
export const TexasImpactCaseStudyTitle3 = (
  <T
    keyName="Texas Impact Case Study Title 3"
    defaultValue="20 Point Growth in Green Valley (TX)"
  />
);
export const CaliforniaImpactCaseStudyTitle = (
  <T keyName="California Impact Case Study Title" defaultValue="46% CAASPP Growth (CA)" />
);
export const InnovationsAcademyCaseStudyTitle = (
  <T
    keyName="Innovations Academy Case Study Title"
    defaultValue="A Virtual School with Double-Digit Growth (CA)"
  />
);
export const SivellsBendCaseStudyTitle = (
  <T
    keyName="Sivells Bend Case Study Title"
    defaultValue="Unprecedented Gains: A Small District's Leap from a C Rating to A in One Year (TX)"
  />
);
export const SouthernCaliforniaImpactCaseStudyTitle = (
  <T
    keyName="Southern California Impact Case Study Title"
    defaultValue="Southern CA – Transformative Writing"
  />
);
export const YsletaMiddleSchoolCaseStudyTitle = (
  <T
    keyName="Ysleta Middle School Case Study Title"
    defaultValue="Accelerating Writing Growth for Emergent Bilingual Students (TX)"
  />
);
export const FlourBluffISDCaseStudyTitle = (
  <T
    keyName="Flour Bluff ISD Case Study Title"
    defaultValue="Small Group Instruction, Big Gains on STAAR (TX)"
  />
);
export const CharlieMarshallCaseStudyTitle = (
  <T
    keyName="Charlie Marshall Case Study Title"
    defaultValue="Five Steps, Real Results: A Principal's Playbook (TX)"
  />
);

const ScrollingLogos = () => {
  const logos = [
    pinecrest,
    vanguard,
    willis,
    dalton,
    psusd,
    texasconnections,
    medina,
    mercer,
    roysecity,
  ];

  // Triple the logos array to create seamless scrolling
  const duplicatedLogos = [...logos, ...logos, ...logos];

  return (
    <ScrollingLogosContainer>
      <StyledH2>
        <T
          keyName="LandingPage.ScrollingLogos.Heading"
          defaultValue="Trusted for High-Quality Instruction Around the World"
        />
      </StyledH2>
      <ScrollingLogosTrack>
        {duplicatedLogos.map((logo, index) => (
          <LogoWrapper key={index}>
            <img src={logo.src} alt={`partner logo ${index + 1}`} />
          </LogoWrapper>
        ))}
      </ScrollingLogosTrack>
    </ScrollingLogosContainer>
  );
};

const VideoSection = () => {
  const metrics = useMetrics(true);
  const isDesktop = useBreakPoint(900);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const { t } = useTranslate();

  return (
    <LandingPageContent>
      {isPlayingVideo ? (
        <iframe
          style={{
            borderRadius: 10,
            marginTop: 40,
            marginBottom: 20,
            zIndex: 1,
            maxWidth: '900px',
            visibility: isPlayingVideo ? 'visible' : 'hidden',
            border: 0,
          }}
          width="80%"
          height={isDesktop ? '500' : '300'}
          src="https://www.youtube.com/embed/I3jj6RS9XVs?si=8IEfd2Z047R46-dx&autoplay=1"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      ) : (
        <PlayButtonOverlay>
          <img
            style={{
              borderRadius: 10,
              marginTop: 40,
              marginBottom: 20,
              zIndex: 1,
              width: '100%',
              maxWidth: '900px',
              visibility: isPlayingVideo ? 'hidden' : 'visible',
            }}
            src={videoCover.src}
            alt={t('Video cover', 'Video cover')}
            onClick={() => {
              setIsPlayingVideo(true);
              metrics.logEvent('FrontPage.VideoClicked', {
                from: 'top',
              });
            }}
          />
          <PlayButton
            aria-label={t(
              'LandingPage.GetStudentsExcited.PlayVideoAriaLabel',
              'Play the video in full screen',
            )}
            onClick={() => setIsPlayingVideo(true)}
          >
            <PlaySvg />
          </PlayButton>
        </PlayButtonOverlay>
      )}
      <ScrollingLogos />
    </LandingPageContent>
  );
};

// Animated words for the hero title
const ANIMATION_INTERVAL = 4000; // 4 seconds

const AnimatedTitleWord = () => {
  const { t } = useTranslate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const animatedWords = [
    t('LandingPage.Hero.AnimatedWord.Lessons', 'Lessons'),
    t('LandingPage.Hero.AnimatedWord.Practice', 'Practice'),
    t('LandingPage.Hero.AnimatedWord.Activities', 'Activities'),
    t('LandingPage.Hero.AnimatedWord.TestPrep', 'Test Prep'),
  ];

  useEffect(() => {
    let mounted = true;

    const interval = setInterval(() => {
      setIsExiting(true);
      setTimeout(() => {
        if (!mounted) return;
        setCurrentIndex(prev => (prev + 1) % animatedWords.length);
        setIsExiting(false);
      }, 400); // Match the animation duration
    }, ANIMATION_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [animatedWords.length]);

  return (
    <AnimatedWordContainer>
      <AnimatedWord $isExiting={isExiting}>{animatedWords[currentIndex]}</AnimatedWord>
    </AnimatedWordContainer>
  );
};

const GetStudentsExcited = ({
  onSignup,
  onBookDemo,
  onProvenOutcomesClick,
  isDesktop,
}: {
  onSignup: () => void;
  onBookDemo: () => void;
  onProvenOutcomesClick: () => void;
  isDesktop: boolean;
}) => {
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoCanPlay = () => {
    setIsVideoLoaded(true);
  };

  const renderTitle = () => (
    <>
      <AnimatedTitleWord />{' '}
      <T keyName="LandingPage.Hero.TitleSuffix" defaultValue="Students Love" />
    </>
  );

  const renderSubtitle = () => (
    <HeroSubtitleText>
      <T keyName="LandingPage.Hero.AllSubjects" defaultValue="All Subjects." />{' '}
      <T keyName="LandingPage.Hero.AllGrades" defaultValue="All Grades." />{' '}
      <ProvenOutcomesLink onClick={onProvenOutcomesClick}>
        <T keyName="LandingPage.Hero.ProvenOutcomes" defaultValue="Proven Outcomes." />
      </ProvenOutcomesLink>
    </HeroSubtitleText>
  );

  const renderButtons = () => (
    <HeroButtonsContainer>
      {!isAuthenticated && (
        <OrangePrimaryButton onClick={onSignup} styles={{ width: '210px' }}>
          <T keyName="LandingPage.Hero.SignUpFree" defaultValue="Sign up - it's free" />
        </OrangePrimaryButton>
      )}
      <WhiteSecondaryButton type="ghost" onClick={onBookDemo} styles={{ width: '210px' }}>
        <T keyName="LandingPage.Hero.BookDemo" defaultValue="Book a demo" />
      </WhiteSecondaryButton>
    </HeroButtonsContainer>
  );

  return (
    <>
      <GetStudentsExcitedSection>
        <BackgroundVideoContainer>
          <BackgroundImage
            src={curiVideoCoverImage.src}
            alt=""
            $isVisible={!isVideoLoaded}
          />
          <BackgroundVideo
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            onCanPlay={handleVideoCanPlay}
          >
            <source src={heroBackgroundVideo} type="video/mp4" />
          </BackgroundVideo>
        </BackgroundVideoContainer>
        <HeroCenterWrapper>
          <TitleContainerOuter>
            <TitleContainerBackground>
              <TitleContainerBackgroundInner />
            </TitleContainerBackground>
            <TitleContainerInner>
              <TitleSubtitleWrapper>
                {isDesktop ? (
                  <WhiteStyledH1>{renderTitle()}</WhiteStyledH1>
                ) : (
                  <WhiteHeaderMobileSectionTitle>
                    {renderTitle()}
                  </WhiteHeaderMobileSectionTitle>
                )}
                {renderSubtitle()}
                {renderButtons()}
              </TitleSubtitleWrapper>
            </TitleContainerInner>
          </TitleContainerOuter>
        </HeroCenterWrapper>
      </GetStudentsExcitedSection>
      <WaveDivider>
        <WaveLandingPageDiv />
      </WaveDivider>
    </>
  );
};

const PlayButtonOverlay = styled.div`
  position: relative;
  width: 80%;
  height: auto;
  max-width: 900px;
  z-index: ${zIndexes('LANDING_PAGE_VIDEO_PLAY_BUTTON_OVERLAY')};
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 30px;
    width: 30px;
  }
  border-radius: 10px;
  height: 70px;
  width: 70px;
  background-color: ${theme.colors.red};
  color: white;
  cursor: pointer;
  outline: none;
  border: none;

  &:hover {
    border: none;
    outline: none;
    background-color: rgba(0, 0, 0, 0.5);
    scale: 1.05;
  }
`;

const OrangePrimaryButton = styled(Button)`
  background-color: ${theme.colors.orange};
  width: 100%;
  height: 42px;
  border-radius: 5px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: none;
  }
`;

const WhiteSecondaryButton = styled(Button)`
  background-color: ${theme.colors.leastTranslucentWhite};
  color: ${theme.colors.black};
  width: 100%;
  height: 42px;
  border-radius: 5px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border: 1px solid ${theme.colors.black};

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight}99;
    color: ${theme.colors.black};
    border: 1px solid ${theme.colors.black};
    box-shadow: none;
  }
`;

const SeeLessons = () => {
  return (
    <>
      <LandingPageSection
        $backgroundGradient={undefined}
        $hasWave={false}
        style={{ gap: 30 }}
      >
        <StarWaveBackground>
          <StarLandingPageBackground />
        </StarWaveBackground>
        <LandingPageContent>
          <div
            style={{
              marginTop: 20,
              zIndex: 1,
              textAlign: 'center',
              width: '100%',
              maxWidth: '1000px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <StyledH2>
              <T
                keyName="LandingPage.SeeLessons.MainHeading"
                defaultValue="Teacher-Paced Lessons Powered by AI"
              />
            </StyledH2>
            <SectionSubtitle>
              <T
                keyName="LandingPage.SeeLessons.Subtitle"
                defaultValue="Get students to look up from their screens and talk to each other. Grounded in pedagogy, Curipod makes the best thing to do, the easiest thing to do."
              />
            </SectionSubtitle>
            <div
              style={{
                width: '100%',
                maxWidth: '1000px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
              }}
            >
              <video
                style={{ width: '100%', height: '100%', borderRadius: '10px' }}
                autoPlay
                loop
                muted
                playsInline
              >
                <source
                  src={`${
                    process.env.NEXT_PUBLIC_APP_ENV === 'dev' ||
                    process.env.NEXT_PUBLIC_APP_ENV === 'pr'
                      ? 'https://images-cdn-staging.curipod.com'
                      : 'https://images-cdn.curipod.com'
                  }/lesson-creation-video.mp4`}
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </LandingPageContent>
      </LandingPageSection>
      <LandingPageSection
        $backgroundGradient={'linear-gradient(180deg, #fff7ef 18.2%, #ffa154 93.41%)'}
        $hasWave={false}
      >
        <LandingPageContent>
          <StyledH3>
            <T
              keyName="LandingPage.SubjectGrid.Heading"
              defaultValue="Get High-Quality Lessons for Any Subject and Any Grade Level"
            />
          </StyledH3>
          <SectionSubtitle>
            <T
              keyName="LandingPage.SubjectGrid.Subtitle"
              defaultValue="Complete with slides, interactive activities, and real-time AI feedback for students—On any topic, in any language, and aligned to your standards and instructional materials."
            />
          </SectionSubtitle>
          <SubjectGrid>
            <SubjectItem>
              <LandingPageSubjectELASvg />
              <h4>
                <T keyName="LandingPage.SubjectGrid.ELA" defaultValue="ELA" />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectMathSvg />
              <h4>
                <T keyName="LandingPage.SubjectGrid.Math" defaultValue="Math" />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectSocialStudiesSvg />
              <h4>
                <T
                  keyName="LandingPage.SubjectGrid.SocialStudies"
                  defaultValue="Social Studies"
                />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectScienceSvg />
              <h4>
                <T keyName="LandingPage.SubjectGrid.Science" defaultValue="Science" />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectWorldLanguagesSvg />
              <h4>
                <T
                  keyName="LandingPage.SubjectGrid.WorldLanguages"
                  defaultValue="World Languages"
                />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectCareerSvg />
              <h4>
                <T
                  keyName="LandingPage.SubjectGrid.CareerTechnical"
                  defaultValue="Career & Technical"
                />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectSELSvg />
              <h4>
                <T keyName="LandingPage.SubjectGrid.SEL" defaultValue="SEL" />
              </h4>
            </SubjectItem>
            <SubjectItem>
              <LandingPageSubjectMoreSvg />
              <h4>
                <T keyName="LandingPage.SubjectGrid.AndMore" defaultValue="And More!" />
              </h4>
            </SubjectItem>
          </SubjectGrid>
        </LandingPageContent>
      </LandingPageSection>
    </>
  );
};

const SubjectGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 1000px;
  width: 100%;
  margin: 40px auto;
`;

const SubjectItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  text-align: center;
  width: 200px;
  padding: 10px 10px;
  background-color: ${theme.colors.white}99;
  border-radius: 12px;
  transition: transform 0.2s ease;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  svg {
    width: 60px;
    height: 60px;
    color: ${theme.colors.orange};
  }

  h4 {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
  }
`;

const CaseStudyCollapse = styled(Collapse)`
  font-size: 18px;
  font-family: 'Nunito';
  border: none;
  padding: 10px;
  background: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 12px;
  max-width: 900px;
  margin: 20px auto;
  width: 100%;
  z-index: 1;

  p {
    margin: 0;
  }

  svg {
    width: 18px;
    height: 18px;
  }
  .ant-collapse-item .ant-collapse-header {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 22px;

    @media (max-width: 600px) {
      font-size: 16px;
    }
  }

  .ant-collapse-content {
    border: none;
    background: transparent;
  }

  .ant-collapse-content-box {
    padding: 0px 10px 20px 10px;
  }

  .ant-collapse-item {
    border: none;
    border-radius: 0px !important;
  }
  .ant-collapse-item.ant-collapse-item.ant-collapse-item-active {
    border-bottom: none;
  }

  .ant-collapse-item:last-child > .ant-collapse-content {
    border-radius: 10px;
  }
`;

const CaseStudyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 600px) {
    p {
      font-size: 14px;
    }
  }
`;

const ReadFullReportButton = styled(Button)`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
  width: fit-content;
  margin: 0px 0px 10px -20px;

  svg {
    width: 16px;
    height: 16px;
    margin: 0px;
    margin-left: 5px;
  }
`;

const TestPrep = () => {
  const isLargerThan800 = useBreakPoint(800);
  const metrics = useMetrics(true);
  const { t } = useTranslate();
  return (
    <LandingPageSection
      $backgroundGradient={undefined}
      $hasWave={false}
      style={{ paddingBottom: 50, padding: '0 10px' }}
    >
      <LandingPageContent>
        <StyledH2>
          <T
            keyName="LandingPage.TestPrep.MainHeading"
            defaultValue="Test Prep Driving Double-Digit Growth"
          />
        </StyledH2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: '70%',
              maxWidth: 500,
              objectFit: 'contain',
              marginRight: isLargerThan800 ? '-15%' : '0%',
            }}
          >
            <img
              style={{ width: '100%' }}
              src={testPrep.src}
              alt={t('Test Prep Report example', 'Test Prep Report example')}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            <SectionSubtitle
              style={{ flex: 1, display: 'flex', alignItems: 'start', maxWidth: 500 }}
            >
              <T
                keyName="LandingPage.TestPrep.Subtitle"
                defaultValue="On Curipod, students are excited to write, get rubric-aligned AI feedback, reflect, and revise in real time—building test-ready skills and confidence."
              />
            </SectionSubtitle>
          </div>
        </div>
        <SectionSubtitle
          style={{
            margin: '20px 0px 0px 0px',
            fontSize: 26,
            textAlign: 'center',
            fontWeight: 800,
          }}
        >
          <T
            keyName="LandingPage.TestPrep.ReadImpactStudies"
            defaultValue="Read our impact studies to learn more."
          />
        </SectionSubtitle>
      </LandingPageContent>
      <CaseStudyCollapse accordion>
        <Panel header={TexasImpactCaseStudyTitle} key="test prep 1">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy1.Content"
                defaultValue="How one teacher found a way to do the impossible—engage every student in writing and provide instant feedback on each writing piece—an approach that spread across 6th-grade classrooms, driving unprecedented achievement."
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', { study: 'south-texas' });
                window.open('/case-studies/curipod-case-study-south-texas', '_blank');
              }}
              icon={<ExternalLinkSvg />}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
        <Divider />
        <Panel header={TexasImpactCaseStudyTitle2} key="test prep 2">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy2.Content"
                defaultValue="From a 6th grade experiment to school-wide movement, Curipod made it easier for teachers to do what they do best—and see double-digit impact of their efforts in student achievement."
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', {
                  study: 'south-east-texas',
                });
                window.open(
                  '/case-studies/curipod-case-study-south-east-texas',
                  '_blank',
                );
              }}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
        <Divider />
        <Panel header={CaliforniaImpactCaseStudyTitle} key="test prep 3">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy3.Content"
                defaultValue="How a group of veteran teachers in Southern California finally found a tech tool that adds value to their instruction, driving a double-digit increase in student achievement."
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', { study: 'california' });
                window.open('/case-studies/california-writing-achievement', '_blank');
              }}
              icon={<ExternalLinkSvg />}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
        <Divider />
        <Panel header={SivellsBendCaseStudyTitle} key="4">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy4.Content"
                defaultValue="How one teacher in Sivells Bend ISD reached <strong>100% Meets & Masters</strong> on STAAR."
                params={{
                  strong: <strong />,
                }}
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', { study: 'sivellsbend' });
                window.open('/case-studies/curipod-case-study-sivells-bend', '_blank');
              }}
              icon={<ExternalLinkSvg />}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
        <Divider />
        <Panel header={SouthernCaliforniaImpactCaseStudyTitle} key="5">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy5.Content"
                defaultValue="How Two 5th-Grade Teachers Beat the Benchmark with Curipod"
                params={{
                  strong: <strong />,
                }}
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', {
                  study: 'southern-california',
                });
                window.open('/case-studies/curipod-case-study-southern-ca', '_blank');
              }}
              icon={<ExternalLinkSvg />}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
        <Divider />
        <Panel header={YsletaMiddleSchoolCaseStudyTitle} key="6">
          <CaseStudyContent>
            <p>
              <T
                keyName="LandingPage.TestPrep.CaseStudy6.Content"
                defaultValue="For Ysleta Middle School, Curipod provided what teachers had always wanted: a way to fit best
  practices into limited time, to give feedback that mattered, and to see growth in every student."
                params={{
                  strong: <strong />,
                }}
              />
            </p>
            <ReadFullReportButton
              type="linkStyle"
              onClick={() => {
                metrics.logEvent('FrontPage.ReadCaseStudy', { study: 'ysleta' });
                window.open(
                  '/case-studies/curipod-case-study-ysleta-bilingual',
                  '_blank',
                );
              }}
              icon={<ExternalLinkSvg />}
            >
              <T
                keyName="LandingPage.TestPrep.ReadFullReport"
                defaultValue="Read full report"
              />
            </ReadFullReportButton>
          </CaseStudyContent>
        </Panel>
      </CaseStudyCollapse>
    </LandingPageSection>
  );
};

const QuoteText = styled.p<{ $fontSize?: number }>`
  font-size: ${({ $fontSize }) => $fontSize || 24}px;
  font-weight: 700;
  margin-bottom: 0px;
  max-width: 900px;
  font-weight: 700;
  line-height: 1.4;
  overflow-y: auto;
  max-height: 100%;

  @media (max-width: 700px) {
    font-size: ${({ $fontSize }) => ($fontSize ? $fontSize - 4 : 20)}px;
  }
`;

const teacherQuotes: {
  quote: React.ReactNode;
  image: React.ReactNode;
  name: React.ReactNode;
}[] = [
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote1"
          defaultValue="Students love using Curipod because they will walk away with some sort of feedback. Whether it's me on the teacher monitoring tool, their peers reviewing and voting on their answers, or AI giving them feedback to improve, <strong>Curipod sparks a natural desire for every student to do their very best.</strong>"
          params={{
            strong: <strong />,
          }}
        />
      </QuoteText>
    ),
    image: <img src={claudia.src} alt="Claudia Garcia" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher1.Name"
          defaultValue="Claudia Garcia, ELAR Teacher"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote2"
          defaultValue="Love this tool for student engagement! From short answer response to drawings, <strong>this tech tool is at the top of my list for student engagement</strong> with content!"
          params={{
            strong: <strong />,
          }}
        />
      </QuoteText>
    ),
    image: <img src={amy.src} alt="Amy Clayton" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher2.Name"
          defaultValue="Amy Clayton, ESL Specialist"
        />
      </p>
    ),
  },

  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote3"
          defaultValue="<strong>Even the most reluctant students were leaning forward, wanting to write.</strong> Junior high students can be reluctant to participate, but not with Curipod! They love getting the immediate feedback from AI with the chance to improve."
          params={{
            strong: <strong />,
          }}
        />
      </QuoteText>
    ),
    image: <img src={becky.src} alt="Becky Canter" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher3.Name"
          defaultValue="Becky Canter, Middle School ELL Teacher"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote4"
          defaultValue="The AI feedback with scoring for test prep is so good! I've never seen students more excited to write!"
        />
      </QuoteText>
    ),
    image: <img src={medina.src} alt="J.Tiemann" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher4.Name"
          defaultValue="J.Tiemann, 4th Grade Teacher, Medina Valley ISD"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote5"
          defaultValue="In over 15 years as a tech trainer, I don't know that I've seen a platform catch on as quickly as Curipod. It has been a life saver! It allows me to create decks with content and activities that are standards-based in about a minute. The students are engaged and asking for more!"
        />
      </QuoteText>
    ),
    image: <img src={karyn.src} alt="Karyn Fillhart" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher5.Name"
          defaultValue="Karyn Fillhart Educational Technology Specialist"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote6"
          defaultValue='The first time I ran a Curipod in my class, I had a student who refused to work with a partner. She was very shy. She tried the Curipod on her own and after class confided me in me, "That was really hard." (Mystery Mission). I replied "That&apos;s why I suggest working with a partner. Two heads are better than one!". The next day, we tried a Mission again, This time, my shy student partnered up— and beat the Mission! <strong>She was beaming with pride after her solution was voted to the top. Curipod is not only a game-changer for student learning, but also for building student confidence in their own voice!</strong>'
          params={{
            strong: <strong />,
          }}
        />
      </QuoteText>
    ),
    image: <img src={rachel.src} alt="Rachel Kent" />,
    name: (
      <T
        keyName="LandingPage.QuotesSection.Teacher6.Name"
        defaultValue="Rachel Kent, Grade 12 CTE Teacher, International Preparatory, Buffalo"
      />
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote7"
          defaultValue='I used Curipod to get my 5th graders STAAR-ready in reading, especially for short and extended constructed responses. They were hooked from the start — the interactive prompts and live feedback made them want to revise and improve. One student said, "Can we do this every day?" <strong>Their confidence grew with every slide, and for once, writing felt exciting — not stressful.</strong>'
          params={{
            strong: <strong />,
          }}
        />
      </QuoteText>
    ),
    image: <img src={salo.src} alt="Salo Donson" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher7.Name"
          defaultValue="Salo Donson, 5th Grade Dual Language Teacher, Fort Worth ISD"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote8"
          defaultValue="I am a teacher of 15 years who, until recently, has not delved deeply into tech in the classroom, and here I am encouraging colleagues to come on in and see Curipod! The time saved and the improvement in my lesson materials is tangible."
        />
      </QuoteText>
    ),
    image: <img src={aileen.src} alt="Aileen Wallace" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher8.Name"
          defaultValue="Aileen Wallace, Modern Studies, Graeme High School, Scotland"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote9"
          defaultValue="They collaborate, they create, and more importantly…they talk! That's why Curipod hit me right in the feels. It brings that same spark into academic lessons. It's dynamic, it's real-time, and it has one magical superpower: it gets my 8th graders to actually respond to me. (I'm not kidding sometimes I think Curipod is the only reason they acknowledge I exist!)"
        />
      </QuoteText>
    ),
    image: <img src={erika.src} alt="Erika Sandstrom" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher9.Name"
          defaultValue="Erika Sandstrom, Digital Media Teacher/Digital learning Coach, Peabody Public Schools"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote10"
          defaultValue="Curipod met me at the intersection of urgency and innovation. In just one month, it transformed my students from reluctant writers to confident, curious thinkers. Writing became something they wanted to do, and our end of year test results proved the power of that shift!"
        />
      </QuoteText>
    ),
    image: <img src={sequoia.src} alt="Sequoia Ford" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher10.Name"
          defaultValue="Sequoia Ford, 3rd Grade Teacher, Mansfield ISD"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote11"
          defaultValue="This is what I love about Curipod. It provides powerful tools to help teachers meet the needs of their students. It keeps the teacher in the loop and makes sure students are engaged with each other and not just their device."
        />
      </QuoteText>
    ),
    image: <img src={dave.src} alt="Dave Hinrichs" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher11.Name"
          defaultValue="Dave Hinrichs, Director of Digital Platforms and Pedagogy, Johnston County Public Schools"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote12"
          defaultValue="Curipod works because it assists the teacher without trying to replace the teacher… It helps teachers and students connect, to let a teacher know their students' thinking process, and to provide feedback soon enough for students—and teachers—to pivot."
        />
      </QuoteText>
    ),
    image: <img src={karyn.src} alt="Karyn Fillhart" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher12.Name"
          defaultValue="Karyn Fillhart, Educational Technology Specialist, Chino Valley Unified School District"
        />
      </p>
    ),
  },
  {
    quote: (
      <QuoteText>
        <T
          keyName="LandingPage.QuotesSection.StudentFeedback.Quote13"
          defaultValue="I love how Curipod flips the narrative by focusing on learning as a social, shared experience where every student's voice matters. &quot;Curipod isn't just another digital tool—it's a platform that puts community, curiosity, and critical thinking at the center of learning."
        />
      </QuoteText>
    ),
    image: <img src={salo.src} alt="Salo Donson" />,
    name: (
      <p>
        <T
          keyName="LandingPage.QuotesSection.Teacher13.Name"
          defaultValue="Salo Donson, 5th Grade Dual Language Teacher, Fort Worth ISD"
        />
      </p>
    ),
  },
];

const QuotesSection = ({ onSignup }: { onSignup: () => void }) => {
  const [visibleQuoteIndex, setVisibleQuoteIndex] = useState(0);
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleQuoteIndex(prevIndex => (prevIndex + 1) % teacherQuotes.length);
    }, 60000); // Change quote every 1 minute

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <LandingPageSection
        $backgroundGradient={'linear-gradient(180deg, #fff7ef 18.2%, #ffa154 93.41%)'}
        $hasWave={true}
      >
        <StarWaveBackground>
          <WaveStarLandingPageBackground />
        </StarWaveBackground>
        <LandingPageContent>
          <StyledH2>
            <T
              keyName="LandingPage.QuotesSection.MainHeading"
              defaultValue="What Are Teachers Saying?"
            />
          </StyledH2>
          {teacherQuotes.map((quote, i) => (
            <QuoteCard
              isVisible={i === visibleQuoteIndex}
              key={i}
              quote={quote.quote}
              image={quote.image}
              name={quote.name}
            />
          ))}

          <div
            style={{
              display: 'flex',
              gap: 3,
              justifyContent: 'center',
              marginTop: 0,
              marginBottom: 50,
            }}
          >
            {teacherQuotes.map((_, i) => (
              <DotButton
                onClick={() => setVisibleQuoteIndex(i)}
                key={i}
                $isActive={i === visibleQuoteIndex}
              />
            ))}
          </div>
          {!isAuthenticated && (
            <WhiteSecondaryButton onClick={() => onSignup()} styles={{ width: '210px' }}>
              <T
                keyName="LandingPage.QuotesSection.SignUpForFree"
                defaultValue="Sign Up For Free"
              />
            </WhiteSecondaryButton>
          )}
        </LandingPageContent>
      </LandingPageSection>
    </>
  );
};

const QuoteCard = ({
  quote,
  image,
  name,
  isVisible,
}: {
  quote: React.ReactNode;
  image: React.ReactNode;
  name: React.ReactNode;
  isVisible: boolean;
}) => {
  // Calculate font size based on quote length
  const quoteText = (quote as any).props.children.props.defaultValue;
  const quoteLength = quoteText.length;
  let fontSize = 24; // Default max size

  if (quoteLength > 500) {
    fontSize = 16;
  } else if (quoteLength > 300) {
    fontSize = 18;
  } else if (quoteLength > 200) {
    fontSize = 20;
  } else if (quoteLength > 100) {
    fontSize = 24;
  }

  return (
    <QuoteCardContainer $isVisible={isVisible}>
      <IconWrapper
        cursor={'default'}
        style={{ position: 'absolute', top: -25, left: 20 }}
        height="50px"
        width="50px"
      >
        <QuoteSvg />
      </IconWrapper>
      <div style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
        {React.cloneElement(quote as React.ReactElement, { $fontSize: fontSize })}
      </div>
      <QuoterContainer>
        <QuoteImage>{image}</QuoteImage>
        <QuoteName>{name}</QuoteName>
      </QuoterContainer>
    </QuoteCardContainer>
  );
};

const QuoteCardContainer = styled.div<{ $isVisible: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: space-between;
  align-items: start;
  max-width: 1000px;
  height: 350px;
  width: 100%;
  padding-bottom: 40px;
  z-index: 1;
  background: ${theme.colors.white};
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  @media (max-width: 500px) {
    height: 400px;
  }

  ${({ $isVisible }) =>
    !$isVisible &&
    css`
      display: none;
    `}
`;

const QuoteImage = styled.div`
  object-fit: cover;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;

  img {
    width: 100%;
  }

  @media (max-width: 700px) {
    width: 50px;
    height: 50px;
  }
`;

const QuoterContainer = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  align-items: center;
  justify-content: center;

  @media (max-width: 700px) {
    grid-template-columns: 50px 1fr;
  }
`;

const QuoteName = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 0px;

  margin-left: 20px;
  z-index: 1;

  p {
    margin-bottom: 0px;
  }

  a {
    text-decoration: underline;
    color: ${theme.colors.greyDark};
  }

  a:hover {
    text-decoration: none;
  }

  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

const DotButton = styled(Button)<{ $isActive: boolean }>`
  visibility: visible !important;
  margin: 0 5px;
  width: 10px !important;
  height: 10px;
  border-radius: 50%;
  padding: 0px;
  background-color: #ececec;

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.white};
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: ${theme.colors.white};
      box-shadow: 1.62951px 1.62951px 16.2951px rgba(0, 0, 0, 0.2);
    `}
`;

const ChecklistSection = () => {
  return (
    <>
      <LandingPageSection
        $backgroundGradient={'linear-gradient(180deg, #FFF7EF 18.2%, #FFC99C 58.44%)'}
        $hasWave={true}
      >
        <LandingPageContent>
          <ul style={{ padding: '0px', margin: '20px auto' }}>
            <ChecklistItem>
              <IconWrapper
                cursor={'default'}
                height="40px"
                width="40px"
                color={theme.colors.green}
              >
                <CheckSvg />
              </IconWrapper>
              <p>
                <T
                  keyName="LandingPage.ChecklistSection.AlignedToStandards"
                  defaultValue="Aligned to standards and state assessments"
                />
              </p>
            </ChecklistItem>
            <ChecklistItem>
              <IconWrapper
                cursor={'default'}
                height="40px"
                width="40px"
                color={theme.colors.green}
              >
                <CheckSvg />
              </IconWrapper>
              <p>
                <T
                  keyName="LandingPage.ChecklistSection.SafeAndSecure"
                  defaultValue="Safe and secure (FERPA & COPPA Compliant)"
                />
              </p>
            </ChecklistItem>
            <ChecklistItem>
              <IconWrapper
                cursor={'default'}
                height="40px"
                width="40px"
                color={theme.colors.green}
              >
                <CheckSvg />
              </IconWrapper>
              <p>
                <T
                  keyName="LandingPage.ChecklistSection.TeacherDriven"
                  defaultValue="Teacher-driven lessons - no student accounts needed"
                />
              </p>
            </ChecklistItem>
            <ChecklistItem>
              <IconWrapper
                cursor={'default'}
                height="40px"
                width="40px"
                color={theme.colors.green}
              >
                <CheckSvg />
              </IconWrapper>
              <p>
                <T
                  keyName="LandingPage.ChecklistSection.IntentionalAI"
                  defaultValue="Intentional integration of AI"
                />
              </p>
            </ChecklistItem>
            <ChecklistItem>
              <IconWrapper
                cursor={'default'}
                height="40px"
                width="40px"
                color={theme.colors.green}
              >
                <CheckSvg />
              </IconWrapper>
              <p>
                <T
                  keyName="LandingPage.ChecklistSection.LessonsDesignedByEducators"
                  defaultValue="Lessons designed by educators,"
                />{' '}
                <Link href="/learning-principles" passHref prefetch={false}>
                  <a target="__blank" rel="noopener noreferrer">
                    <T
                      keyName="LandingPage.ChecklistSection.LearnMore"
                      defaultValue="learn more about our learning principles here."
                    />
                  </a>
                </Link>
              </p>
            </ChecklistItem>
          </ul>
        </LandingPageContent>
      </LandingPageSection>
      <WaveDivider>
        <WaveLandingPageDiv3 />
      </WaveDivider>
    </>
  );
};

const ChecklistItem = styled.li`
  display: flex;
  align-items: start;
  gap: 10px;
  font-weight: 700;
  margin-bottom: 0px;
  max-width: 900px;

  p {
    font-size: 30px;
  }

  a {
    text-decoration: underline;
  }

  a:hover {
    text-decoration: none;
  }

  @media (max-width: 700px) {
    p {
      font-size: 22px;
    }
  }
`;

const InspireCuriositySection = ({ onSignup }: { onSignup: () => void }) => {
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const metrics = useMetrics(true);

  return (
    <LandingPageSection
      style={{ minHeight: 300, zIndex: 0 }}
      $backgroundGradient={undefined}
      $hasWave={true}
    >
      <StarWaveBackground style={{ height: 300, overflow: 'hidden' }}>
        <StarLandingPageBackground />
      </StarWaveBackground>
      <LandingPageContent>
        <StyledH2>
          <T
            keyName="LandingPage.InspireCuriositySection.MainHeading"
            defaultValue="Feel the Buzz. Become a Curipod Classroom."
          />
        </StyledH2>
        <ResponsiveCTA>
          {!isAuthenticated && (
            <div
              style={{
                display: 'grid',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <span
                style={{
                  width: '100%',
                  textAlign: 'center',
                  color: theme.colors.black,
                  fontWeight: 'bold',
                }}
              >
                <T
                  keyName="LandingPage.InspireCuriositySection.TeachersLabel"
                  defaultValue="Teachers"
                />
              </span>
              <OrangePrimaryButton styles={{ width: '210px' }} onClick={() => onSignup()}>
                <T
                  keyName="LandingPage.InspireCuriositySection.SignUpForFree"
                  defaultValue="Sign Up For Free"
                />
              </OrangePrimaryButton>
            </div>
          )}
          <div
            style={{
              display: 'grid',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                width: '100%',
                textAlign: 'center',
                color: theme.colors.black,
                fontWeight: 'bold',
              }}
            >
              <T
                keyName="LandingPage.InspireCuriositySection.AdministratorsLabel"
                defaultValue="Administrators"
              />
            </span>
            <a href="/quotes/" target="_curipod_quote">
              <WhiteSecondaryButton
                type="ghost"
                styles={{ width: '210px' }}
                onClick={() => {
                  metrics.logEvent('FrontPage.QuoteClicked', {
                    from: 'top',
                  });
                }}
              >
                <T
                  keyName="LandingPage.InspireCuriositySection.GetAQuote"
                  defaultValue="Get a Quote"
                />
              </WhiteSecondaryButton>
            </a>
          </div>
        </ResponsiveCTA>
      </LandingPageContent>
    </LandingPageSection>
  );
};

const ExamplesSection = styled.section`
  width: 100%;
  max-width: 1000px;
  margin: 0px auto 20px auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
  padding: 0 20px;
  justify-items: center;
  border-radius: 16px;
  gap: 18px;

  img {
    width: 100%;
    max-width: 350px;
    height: auto;
    border-radius: 10px;
    margin-bottom: 10px;
  }

  h3 {
    font-size: 26px;
    text-align: center;
    font-weight: 800;
    margin: 0;
    color: ${theme.colors.black};
  }

  p {
    font-size: 20px;
    text-align: center;
    max-width: 600px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const Examples = ({ onSignup }: { onSignup: () => void }) => {
  const metrics = useMetrics(true);
  const isLargerThan900 = useBreakPoint(900);
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';

  return (
    <LandingPageSection
      style={{ minHeight: 300, zIndex: 0 }}
      $backgroundGradient={'linear-gradient(180deg, #ffa154 10%, #fff7ef 90%)'}
      $hasWave={true}
    >
      <LandingPageContent>
        <StyledH2>
          <T
            keyName="LandingPage.ExamplesSection.MainHeading"
            defaultValue="See Why 12 Million Students Are Excited to Come to Class"
          />
        </StyledH2>
      </LandingPageContent>
      <ExamplesSection>
        {isLargerThan900 && (
          <>
            <img src={slidesImg.src} alt="Slides example" />
            <img src={feedbackImg.src} alt="AI Feedback example" />
            <h3>
              <T
                keyName="LandingPage.ExamplesSection.ActiveParticipationHeading"
                defaultValue="100% Active Participation, Built In"
              />
            </h3>
            <h3>
              <T
                keyName="LandingPage.ExamplesSection.AIFeedbackHeading"
                defaultValue="Real-Time AI Feedback at Every Step"
              />
            </h3>
            <SectionSubtitle>
              <T
                keyName="LandingPage.ExamplesSection.ActiveParticipationSubtitle"
                defaultValue="Curipod activities lower barriers to participation. All the things that great teachers do are woven into the fabric of every lesson."
              />
            </SectionSubtitle>
            <SectionSubtitle>
              <T
                keyName="LandingPage.ExamplesSection.AIFeedbackSubtitle"
                defaultValue="Students get specific and actionable real-time AI feedback while the learning is still fresh, helping them reflect, revise, and grow."
              />
            </SectionSubtitle>
          </>
        )}
        {!isLargerThan900 && (
          <>
            <img src={slidesImg.src} alt="Slides example" />
            <h3>
              <T
                keyName="LandingPage.ExamplesSection.ActiveParticipationHeading"
                defaultValue="100% Active Participation, Built In"
              />
            </h3>
            <SectionSubtitle>
              <T
                keyName="LandingPage.ExamplesSection.ActiveParticipationSubtitle"
                defaultValue="Curipod activities lower barriers to participation. All the things that great teachers do are woven into the fabric of every lesson."
              />
            </SectionSubtitle>

            <img src={feedbackImg.src} alt="AI Feedback example" />
            <h3>
              <T
                keyName="LandingPage.ExamplesSection.AIFeedbackHeading"
                defaultValue="Real-Time AI Feedback at Every Step"
              />
            </h3>
            <SectionSubtitle>
              <T
                keyName="LandingPage.ExamplesSection.AIFeedbackSubtitle"
                defaultValue="Students get specific and actionable real-time AI feedback while the learning is still fresh, helping them reflect, revise, and grow."
              />
            </SectionSubtitle>
          </>
        )}
      </ExamplesSection>
      <LandingPageContent>
        <div
          style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '40px',
          }}
        >
          {!isAuthenticated && (
            <OrangePrimaryButton styles={{ width: '210px' }} onClick={() => onSignup()}>
              <T
                keyName="LandingPage.ExamplesSection.SignUpForFree"
                defaultValue="Sign Up For Free"
              />
            </OrangePrimaryButton>
          )}
          <a href="/quotes/" target="_curipod_quote">
            <WhiteSecondaryButton
              type="ghost"
              styles={{ width: '210px' }}
              onClick={() => {
                metrics.logEvent('FrontPage.QuoteClicked', {
                  from: 'top',
                });
              }}
            >
              <T
                keyName="LandingPage.ExamplesSection.GetAQuote"
                defaultValue="Get a Quote"
              />
            </WhiteSecondaryButton>
          </a>
        </div>
      </LandingPageContent>
    </LandingPageSection>
  );
};

const LandingPageHome = () => {
  const router = useRouter();
  useLocale();
  const metrics = useMetrics(true);
  const isDesktop = useBreakPoint(768);

  const handleSignupClicked = useCallback(() => {
    metrics.logEvent('FrontPage.SignupClicked', {
      from: 'tryForFree',
    });

    const trackingParams = getTrackingParams();
    const query: Record<string, string> = {
      callback: `/`,
    };

    // Fallback for TrackingParamsProvider: pass in URL when localStorage may be unavailable (e.g. private browsing)
    if (trackingParams.fbclid) {
      query.fbclid = trackingParams.fbclid;
    }
    if (trackingParams.ttclid) {
      query.ttclid = trackingParams.ttclid;
    }

    router.push({
      pathname: '/signup',
      query,
    });
  }, [metrics, router]);

  const handleBookDemo = useCallback(() => {
    metrics.logEvent('FrontPage.BookDemoClicked', {
      from: 'hero',
    });
    window.open('/quotes/', '_curipod_quote');
  }, [metrics]);

  const handleProvenOutcomesClick = useCallback(() => {
    metrics.logEvent('FrontPage.ProvenOutcomesClicked');
    // Dispatch custom event to trigger Impact & Efficacy dropdown in header
    window.dispatchEvent(new CustomEvent('openImpactEfficacyDropdown'));
  }, [metrics]);

  return (
    <Container>
      <GetStudentsExcited
        onSignup={handleSignupClicked}
        onBookDemo={handleBookDemo}
        onProvenOutcomesClick={handleProvenOutcomesClick}
        isDesktop={isDesktop}
      />
      <VideoSection />
      <SeeLessons />
      <Examples onSignup={handleSignupClicked} />
      <TestPrep />
      <QuotesSection onSignup={handleSignupClicked} />
      <ChecklistSection />
      <InspireCuriositySection onSignup={handleSignupClicked} />
    </Container>
  );
};

export default LandingPageHome;
